import React from "react";
import { CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useSubscriptionPaymentUrl } from "@/components/pages/hotels/hooks/useHotelSubscriptions";
import { useAdminImpersonation } from "@/hooks/useAdminImpersonation";
import { useLogout } from "@/hooks/useLogout";
import type { UserProfile } from "@/services/authService";
import { Role } from "@/types/user";

interface SubscriptionLockOverlayProps {
  isLoading: boolean;
  userProfile: UserProfile | null;
}

const PAYMENT_POLLING_STORAGE_KEY =
  "hotel-subscription-payment-processing-hotel-id";
const PAYMENT_POLLING_STARTED_AT_STORAGE_KEY =
  "hotel-subscription-payment-processing-started-at";

export const SubscriptionLockOverlay: React.FC<
  SubscriptionLockOverlayProps
> = ({ userProfile, isLoading }) => {
  const { t } = useTranslation();
  const { handleLogout } = useLogout();
  const { exitImpersonation, isImpersonating } = useAdminImpersonation();
  const access = userProfile?.access ?? null;
  const { mutateAsync: getPaymentUrl, isPending: isOpeningPayment } =
    useSubscriptionPaymentUrl();
  const role = userProfile?.role;
  const signOutLabel = isImpersonating
    ? t("layout.userMenu.exitImpersonation")
    : t("layout.userMenu.signOut");
  const handleAuthAction = isImpersonating ? exitImpersonation : handleLogout;
  const isHotelScopedRole =
    role === Role.HOTEL_ADMIN ||
    role === Role.FRONT_DESK ||
    role === Role.STAFF;
  const tenantSubscriptionId = access?.tenantSubscriptionId ?? null;
  const canOpenPayment =
    role === Role.HOTEL_ADMIN && tenantSubscriptionId !== null;
  const shouldShow =
    isHotelScopedRole && access !== null && !access.hasActiveSubscription;

  if (isLoading || !shouldShow) {
    return null;
  }

  const handlePayment = async () => {
    if (!tenantSubscriptionId) {
      return;
    }

    try {
      const response = await getPaymentUrl({
        subscriptionId: tenantSubscriptionId,
        returnUrl: new URL(
          "/billing-subscriptions",
          window.location.origin,
        ).toString(),
      });

      if (!response.url) {
        toast.error(t("hotels.subscriptions.paymentUrlUnavailable"));
        return;
      }

      if (userProfile?.profile?.hotelId) {
        window.sessionStorage.setItem(
          PAYMENT_POLLING_STORAGE_KEY,
          userProfile.profile.hotelId,
        );
        window.sessionStorage.setItem(
          PAYMENT_POLLING_STARTED_AT_STORAGE_KEY,
          String(Date.now()),
        );
      }
      window.location.assign(response.url);
    } catch {
      toast.error(t("hotels.subscriptions.paymentUrlError"));
    }
  };

  const renderSubscriptionAction = () => {
    if (canOpenPayment) {
      return (
        <Button
          type="button"
          className="w-full sm:w-fit"
          disabled={isOpeningPayment}
          onClick={() => void handlePayment()}
        >
          {t("layout.subscriptionLock.pay")}
        </Button>
      );
    }

    return (
      <p className="border-border bg-muted text-muted-foreground rounded-lg border p-4 text-sm leading-6">
        {role === Role.HOTEL_ADMIN
          ? t("layout.subscriptionLock.noPaymentLink")
          : t("layout.subscriptionLock.contactHotelAdmin")}
      </p>
    );
  };

  return (
    <div className="bg-background/80 fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <section
        aria-labelledby="subscription-lock-title"
        aria-describedby="subscription-lock-description"
        aria-modal="true"
        className="border-border bg-card text-card-foreground flex w-full max-w-lg flex-col gap-6 rounded-lg border p-6 shadow-xl sm:p-8"
        role="dialog"
      >
        <div className="flex flex-col gap-3">
          <div className="bg-primary/10 text-primary flex size-12 items-center justify-center rounded-lg">
            <CreditCard aria-hidden className="size-6" />
          </div>
          <div className="flex flex-col gap-2">
            <h2
              className="text-xl font-semibold tracking-normal"
              id="subscription-lock-title"
            >
              {t("layout.subscriptionLock.title")}
            </h2>
            <p
              className="text-muted-foreground text-sm leading-6"
              id="subscription-lock-description"
            >
              {t("layout.subscriptionLock.description")}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {renderSubscriptionAction()}
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-fit"
            onClick={() => {
              void handleAuthAction();
            }}
          >
            {signOutLabel}
          </Button>
        </div>
      </section>
    </div>
  );
};
